import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { confirmEmailCode, requestEmailCode, type Me } from "../../lib/api";
import { noteCodeSentTo } from "../../lib/dev";
import { qk } from "../../lib/queries";
import { track } from "../../lib/telemetry";
import { WrongAccount } from "../ui/account";
import { OnboardFace, OnboardShell, SomebodyWaiting, StepDots } from "./steps";

/**
 * STEP 2 OF THE RUN, EMAIL (ruling R-0826-3, `docs/emails.md` Q1).
 *
 * WHY THIS IS A STEP AND NOT A CTA, since that is the change. The address used
 * to be the first of three setup prompts in the console — a thing an owner
 * could dismiss for ever — and everything Nob is obliged to write reads it:
 * receipts, the trial reminder, the dunning ladder, the cancellation
 * acknowledgement, the Morning Nob, a reply crossing back to a knocker. An
 * account that finished onboarding without one is an account the product cannot
 * talk to, and the failure is silent on both sides. So it moved into the run,
 * where a step is answered rather than deferred.
 *
 * WHY IT IS BEFORE THE MONEY STEP. The first thing that follows a card is a
 * receipt, and a receipt with nowhere to go is the one compliance obligation
 * this product cannot discharge quietly (`docs/compliance.md`, mechanism 3).
 *
 * WHY A CODE. An address typed into a form is a claim about a mailbox, not
 * proof of one — a typo is silence and a hostile paste is us mailing a stranger
 * with our own sending reputation behind it. Six digits, ten minutes, five
 * tries. It is NOT an entry point: nothing in the mail signs anybody in, which
 * is the rule R-0826-1 left standing (`docs/emails.md` §1.3).
 *
 * THERE IS NO SKIP AND THERE IS NO "LATER". This is the only screen in the run
 * with no way past it — the trial can be declined, the door step can be stepped
 * aside from, and this cannot, because both of those leave a working product
 * behind and this would not.
 */

export const EMAIL_TITLE = "Where Nob reaches you.";
/** The one line of why. Named things, not a category: an owner recognises post. */
export const EMAIL_LEAD = "We won’t spam you. We literally build anti-spam stuff.";
export const EMAIL_FIELD_LABEL = "Email address";
export const SEND_CODE_LABEL = "Send the code";
export const SENDING_LABEL = "Sending…";
export const CODE_FIELD_LABEL = "6-digit code";
export const CONFIRM_LABEL = "Confirm";
export const CONFIRMING_LABEL = "Checking…";
export const RESEND_LABEL = "Send it again";
export const CHANGE_ADDRESS_LABEL = "Use a different address";

/** Where the code went, in the server's masked form. */
export function sentToLine(masked: string): string {
  return `Six digits, on their way to ${masked}. They work for ten minutes.`;
}
export function resendWaitLabel(seconds: number): string {
  return `Send it again in ${seconds}s`;
}
export function wrongCodeLine(left: number): string {
  return left === 1
    ? "That code is not right. One try left, then Nob sends a new one."
    : `That code is not right. ${left} tries left.`;
}

export const EMAIL_UNREADABLE = "Nob could not read that address.";
export const EMAIL_UNREACHABLE = "Nob is not answering. Try that again.";
export const MAIL_UNAVAILABLE_LINE = "Nob could not get that code into the post. Try that again.";
export const EXPIRED_LINE = "That code has expired. Ask for another.";
export const LOCKED_OUT_LINE = "Too many wrong tries. Ask for a new code.";
export const NO_PENDING_LINE = "There is no code to check. Ask for one.";
/**
 * WHAT A SCREEN THAT DID NOT CARRY THE ATTESTATION IS TOLD (ruling R-0915-2).
 *
 * Both of the server's attestation refusals land here now and they mean the
 * same thing: this screen showed a wording the server cannot name, or showed
 * none at all, and in either case nothing was recorded. There is no longer a
 * refusal for "you did not tick it", because there is nothing to tick — the
 * sentence itself is still the SERVER's and is never written in this file.
 */
export const ATTESTATION_MISMATCH_LINE =
  "This screen is out of date. Reload it, and Nob will ask again.";

type Phase = "address" | "code";

type Props = {
  me: Me;
  /** The step's number and total both come from the path (FR-1.1). */
  knocker: boolean;
  /**
   * People already waiting at this handle (owner item 3). The FIRST screen that
   * can say so: Connect has just answered with the identity, so the run knows
   * whose door is being hung and the server has counted. Zero draws nothing.
   */
  waitingCount: number;
  /**
   * THE AGE ATTESTATION, IN THE SERVER'S WORDS (rulings R-0914-1 (1), R-0915-2).
   * Rendered verbatim beneath the button and hashed exactly as rendered — the
   * client neither composes it nor edits it, for the same reason it does not
   * compose a purchase disclosure. Empty means the run state could not be read,
   * and the step then posts no attestation and is refused by the server, which
   * is the right way for this to fail: nothing is recorded for a sentence
   * nobody was shown.
   */
  attestation: string;
  /** Confirmed: the run moves on. The caller decides where to. */
  onConfirmed: () => void;
};

export function OnboardingEmailStep({
  me,
  knocker,
  waitingCount,
  attestation,
  onConfirmed,
}: Props) {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<Phase>("address");
  /**
   * Prefilled from whatever the account already holds, which for an X-only
   * session is nothing at all. Somebody changing an address should not have to
   * retype the one they are changing.
   */
  const [email, setEmail] = useState(me.contact_email ?? "");
  const [code, setCode] = useState("");
  const [masked, setMasked] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Seconds until another send is allowed. The server's floor, counted down. */
  const [wait, setWait] = useState(0);
  const addressField = useRef<HTMLInputElement>(null);
  const codeField = useRef<HTMLInputElement>(null);

  // The caret goes where the typing goes. A form that changes shape under the
  // pointer and then waits to be clicked is a form that changed for nobody.
  useEffect(() => {
    if (phase === "code") codeField.current?.focus();
    else addressField.current?.focus();
  }, [phase]);

  // One timer for the whole screen, cleared by machinery rather than by memory.
  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const send = useCallback(async () => {
    const address = email.trim();
    if (!address || busy) return;
    setBusy(true);
    setError(null);
    const r = await requestEmailCode(address, attestation);
    setBusy(false);
    if (!r.ok) {
      if (r.code === "TOO_SOON") {
        setWait(r.retry_after_seconds);
        // Already sent, and recently: the screen moves to the code field rather
        // than sitting on a refusal for a thing that has in fact happened.
        setPhase("code");
        return;
      }
      // The server's own sentence where it wrote one; ours only where it did not.
      setError(
        r.message ??
          (r.code === "VALIDATION_ERROR"
            ? EMAIL_UNREADABLE
            : r.code === "MAIL_UNAVAILABLE"
              ? MAIL_UNAVAILABLE_LINE
              : r.code === "AGE_ATTESTATION_REQUIRED" || r.code === "ATTESTATION_MISMATCH"
                ? ATTESTATION_MISMATCH_LINE
                : EMAIL_UNREACHABLE),
      );
      return;
    }
    setMasked(r.to);
    setCode("");
    setWait(r.resend_in_seconds);
    setPhase("code");
    // So the local flow-testing strip can read the code back and a human never
    // has to leave the browser for a terminal. Inert everywhere the kit is not
    // mounted — the route it feeds does not exist on a deployed tier.
    noteCodeSentTo(address);
    track({ name: "onboarding_email_code_sent" });
  }, [attestation, busy, email]);

  const confirm = useCallback(async () => {
    const typed = code.trim();
    if (!typed || busy) return;
    setBusy(true);
    setError(null);
    const r = await confirmEmailCode(typed);
    setBusy(false);
    if (!r.ok) {
      if (r.code === "WRONG_CODE") {
        setError(wrongCodeLine(r.attempts_left));
        setCode("");
        codeField.current?.focus();
        return;
      }
      // Expired, locked out or nothing pending: all three mean "ask for another",
      // which is the only thing this screen can do about any of them — so it goes
      // back to the address, with the resend floor honoured.
      setError(
        r.code === "EXPIRED"
          ? EXPIRED_LINE
          : r.code === "LOCKED_OUT"
            ? LOCKED_OUT_LINE
            : r.code === "NO_PENDING"
              ? NO_PENDING_LINE
              : EMAIL_UNREACHABLE,
      );
      setCode("");
      return;
    }
    // The run moves on off the SERVER's answer: `/me` and the run state are both
    // re-read, and only then is the step counted as answered.
    await queryClient.invalidateQueries({ queryKey: qk.me() });
    await queryClient.invalidateQueries({ queryKey: qk.onboarding() });
    noteCodeSentTo(null);
    track({ name: "onboarding_email_done" });
    // The server hangs the door the moment the email is proved (R-1006-2).
    track({ name: "onboarding_door_created" });
    onConfirmed();
  }, [busy, code, onConfirmed, queryClient]);

  /**
   * The refusal, rendered UNDER THE FIELD THAT EARNED IT rather than at the
   * bottom of the sheet. It used to sit below the two ghost actions, which put
   * "that code is not right" four rows away from the code — a sentence you have
   * to go looking for is a sentence the screen did not really say.
   */
  const refusal = error ? (
    // The spacer is a WRAPPER, not a utility on the paragraph: `.nob-poster-error`
    // sets `margin: 0 0 8px` and poster.css loads after the utilities, so an
    // `mt-*` on the <p> loses. Without it the sentence sits on the input's hard
    // offset shadow instead of under the field.
    <div className="mt-3">
      <p className="nob-poster-error" role="alert">
        {error}
      </p>
    </div>
  ) : null;

  return (
    <OnboardShell>
      <StepDots step={2} knocker={knocker} />
      <OnboardFace mood="watching" />

      <h1 className="nob-poster-title">{EMAIL_TITLE}</h1>
      <p className="nob-poster-lead">{EMAIL_LEAD}</p>
      {/* Under the lead rather than above the headline: the headline is what
          this screen is FOR, and a piece of news above it would be answering a
          question nobody has been asked yet. */}
      <SomebodyWaiting count={waitingCount} />

      {phase === "address" ? (
        <form
          className="mt-4 w-full"
          // The SERVER owns what an address is, and this form prints its
          // sentence. Native constraint validation would swallow the submit and
          // answer with a browser tooltip instead — a refusal in somebody else's
          // words, at a moment Nob never heard about.
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <label htmlFor="onboarding-email" className="sr-only">
            {EMAIL_FIELD_LABEL}
          </label>
          <input
            id="onboarding-email"
            ref={addressField}
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            disabled={busy}
            aria-invalid={error !== null}
            onChange={(e) => setEmail(e.target.value)}
            className="nob-poster-input"
          />
          {refusal}
          <div className="nob-poster-cta">
            <button
              type="submit"
              disabled={busy || email.trim().length === 0}
              className="nob-poster-btn"
            >
              {busy ? SENDING_LABEL : SEND_CODE_LABEL}
            </button>
          </div>
          {/* SMALL PRINT, UNDER THE BUTTON IT DESCRIBES (ruling R-0915-2).
              Pressing the button IS the attestation, so the sentence belongs
              beneath it the way small print always does — a checkbox in front
              of a button asks for the same fact twice. The words are still the
              server's, shown exactly as they arrived and hashed exactly as
              shown; nothing here composes or edits them. */}
          <p className="nob-poster-legal">{attestation}</p>
        </form>
      ) : (
        <form
          className="mt-6 w-full"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void confirm();
          }}
        >
          <p className="nob-poster-footnote">{sentToLine(masked)}</p>
          <label htmlFor="onboarding-email-code" className="sr-only">
            {CODE_FIELD_LABEL}
          </label>
          <input
            id="onboarding-email-code"
            ref={codeField}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            /* The browser's own autofill lane for a code posted to a mailbox.
               `maxLength` is the only client-side rule here: everything about
               what a code IS belongs to the server. */
            maxLength={6}
            /* NO PLACEHOLDER. `000000` in the poster's ink weight reads as a
               value already typed, and a field that looks filled is a field
               nobody fills. The line above says what goes in it. */
            value={code}
            disabled={busy}
            aria-invalid={error !== null}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="nob-poster-input mt-3 text-center font-mono tracking-[0.5em]"
          />
          {refusal}
          <div className="nob-poster-cta">
            <button
              type="submit"
              disabled={busy || code.trim().length === 0}
              className="nob-poster-btn"
            >
              {busy ? CONFIRMING_LABEL : CONFIRM_LABEL}
            </button>
            <button
              type="button"
              disabled={busy || wait > 0}
              onClick={() => void send()}
              className="nob-poster-ghost"
            >
              {wait > 0 ? resendWaitLabel(wait) : RESEND_LABEL}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setError(null);
                setCode("");
                setPhase("address");
              }}
              className="nob-poster-ghost"
            >
              {CHANGE_ADDRESS_LABEL}
            </button>
          </div>
        </form>
      )}

      {/* The wrong-account escape. Sign-in is sticky, so somebody with two X
          accounts can arrive as the wrong one, and this is the first screen
          after Connect where they would notice. */}
      <WrongAccount me={me} className="nob-poster-legal" />
    </OnboardShell>
  );
}
